import { types, cast } from "mobx-state-tree";
import { DepartmentList, initialState as emptyList } from "./department-list";
import { Department, DepartmentSnapshotType, fields, initialState as emptyDepartment } from "./department";
import { Transport } from "modules/common/models/transport";
import { Notificator } from "modules/common/models/notificator";
import { apiUrls } from "modules/common/services/communication/urls";
import { flow } from "modules/common/models/flow";
import { isNewlyCreated } from "modules/common/models/entity";
import { texts } from "modules/common/texts";

/**
 * Отделы: список и карточка. Образец по правилу из Rost.Ui/CLAUDE.md.
 *
 * Карточка (details) — сменяемое поле, а не постоянный узел-«слот»: открыли
 * другой отдел или сохранили новый — в поле встаёт другой узел. Прежний при этом
 * уничтожается, поэтому компоненты берут карточку из стора (store.details), а не
 * получают узел пропом. null — карточка не открыта.
 */
export const DepartmentsStore = types
    .compose(
        Transport,
        Notificator,
        types.model({
            list: DepartmentList,
            details: types.maybeNull(Department),
        })
    )
    .actions((self) => {
        // номер последней загрузки карточки: ответ на устаревший запрос не должен
        // заменить карточку, которую уже открыли следом
        let detailsRun = 0;

        return {
            loadDetails: flow(function* (id: string) {
                const run = ++detailsRun;

                try {
                    const snapshot: DepartmentSnapshotType | null = !id
                        ? null
                        : isNewlyCreated(id)
                          ? emptyDepartment()
                          : yield self.transport.get<DepartmentSnapshotType>(apiUrls.departments.details(id));

                    if (run === detailsRun) {
                        self.details = cast(snapshot);
                    }
                } catch (er) {
                    self.notify.error(er);
                }
            }),

            saveDetails: flow(function* (model: any) {
                const details = self.details;
                if (!details) {
                    return false;
                }

                try {
                    let order: any = model[fields.order];

                    if (order === "") {
                        order = null;
                    } else if (typeof order === "string") {
                        order = parseInt(order, 10);
                    }

                    const snapshot: DepartmentSnapshotType = details.isNewlyCreated
                        ? yield self.transport.put<DepartmentSnapshotType>(apiUrls.departments.create(), {
                              ...model,
                              order,
                          })
                        : yield self.transport.post<DepartmentSnapshotType>(apiUrls.departments.update(details.id), {
                              ...model,
                              order,
                          });

                    // новый отдел получил id от сервера — в карточку встаёт новый узел,
                    // id у прежнего не меняется. Если за время запроса открыли другую
                    // карточку, её не трогаем
                    if (self.details === details) {
                        self.details = cast(snapshot);
                    }
                    self.notify.success(texts.messages.saved);

                    return true;
                } catch (er) {
                    self.notify.error(er);

                    return false;
                }
            }),

            deleteDetails: flow(function* () {
                const details = self.details;
                if (!details || details.isNewlyCreated) {
                    return true;
                }

                const removed: boolean = yield self.list.delete(details.id);
                if (removed && self.details === details) {
                    self.details = null;
                }

                return removed;
            }),
        };
    })
    .named("DepartmentsStore");

export const initialState = (): typeof DepartmentsStore.SnapshotType => ({
    list: emptyList(),
    details: null,
});

export type DepartmentsStoreType = typeof DepartmentsStore.Type;
